import { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { squareToVec3 } from './scene.js';

/**
 * Animated arrow that connects two squares. Originally written for the
 * opponent's last-move origin/destination (single coral arrow); generalised
 * here so the multi-ply look-ahead chain can render one arrow per ply
 * with alternating cyan/mint colours.
 *
 * Animates a 250ms ease-out scale + opacity lerp on mount / when `from` or
 * `to` change. `delayMs` shifts the animation clock forward so a chain of
 * arrows can play out one after another. Honors `prefers-reduced-motion`
 * by snapping to the final state.
 *
 * Returns `null` (no mesh) when `from === to` (degenerate arrow) or when
 * either square is off-board (squareToVec3 returns a value outside the
 * [-3.5, 3.5] x [-3.5, 3.5] board span).
 */
export const ARROW_COLOR = '#ff7a59';
const ARROW_Y = 0.06; // sits above the ring highlights (which top out at 0.04)
const SHAFT_RADIUS = 0.05;
const HEAD_RADIUS = 0.18;
const HEAD_LENGTH = 0.32;
const ANIM_MS = 250;
const FINAL_OPACITY = 0.85;

const Y_UP = new THREE.Vector3(0, 1, 0);

function isOnBoard([x, , z]: [number, number, number]): boolean {
  return x >= -3.5 && x <= 3.5 && z >= -3.5 && z <= 3.5;
}

export function OppArrow({
  from,
  to,
  color = ARROW_COLOR,
  delayMs = 0,
  yOffset = 0,
}: {
  readonly from: string;
  readonly to: string;
  /** Hex colour string. Defaults to coral (PR #96's last-move arrow). The
   *  multi-ply chain overrides this per ply (cyan opponent, mint player). */
  readonly color?: string;
  /** Optional animation delay in ms. Used by PlyArrows to stagger the
   *  chain so each ply fades in after the previous one. */
  readonly delayMs?: number;
  /** Optional vertical offset added to ARROW_Y. Used by PlyArrows to
   *  push deeper plies slightly higher so they don't z-fight with
   *  shallower plies or the last-move arrow. */
  readonly yOffset?: number;
}) {
  const fromPos = squareToVec3(from);
  const toPos = squareToVec3(to);
  // Off-board / degenerate inputs render nothing — same contract as Highlights.
  if (from === to || !isOnBoard(fromPos) || !isOnBoard(toPos)) return null;

  // Build the geometry layout once per (from, to). Both meshes carry a
  // quaternion that maps their default +Y height axis onto the bearing
  // direction; the shaft sits at the from-to midpoint and the cone's apex
  // lands on the destination.
  const layout = useMemo(() => {
    const dx = toPos[0] - fromPos[0];
    const dz = toPos[2] - fromPos[2];
    const length = Math.hypot(dx, dz);
    const shaftLength = Math.max(0, length - HEAD_LENGTH);
    const midX = (fromPos[0] + toPos[0]) / 2;
    const midZ = (fromPos[2] + toPos[2]) / 2;
    const dir = new THREE.Vector3(dx, 0, dz).normalize();
    // Cone center sits HEAD_LENGTH/2 back from the destination along the
    // bearing so its apex lands on `toPos`.
    const headX = toPos[0] - (HEAD_LENGTH / 2) * dir.x;
    const headZ = toPos[2] - (HEAD_LENGTH / 2) * dir.z;
    const q = new THREE.Quaternion().setFromUnitVectors(Y_UP, dir);
    return { midX, midZ, headX, headZ, shaftLength, q };
  }, [fromPos, toPos]);

  const shaftRef = useRef<THREE.Mesh>(null);
  const headRef = useRef<THREE.Mesh>(null);
  const shaftMatRef = useRef<THREE.MeshBasicMaterial>(null);
  const headMatRef = useRef<THREE.MeshBasicMaterial>(null);
  // Absolute wall-clock timestamp the animation should START rendering at.
  // = now + delayMs. Negative `elapsed` means the arrow is still in its
  // pre-delay invisible phase; useFrame clamps elapsed to [0, ANIM_MS].
  const startedAtRef = useRef<number>(performance.now() + delayMs);

  // Reset the animation clock on mount and whenever the props change. The
  // deps include the props that affect the animation: from/to (new arrow),
  // delayMs (new stagger position), and color is intentionally NOT a dep —
  // a colour change shouldn't replay the animation.
  useEffect(() => {
    startedAtRef.current = performance.now() + delayMs;
    // Apply the colour to both materials up-front so the first frame is
    // already the right colour (the useFrame loop only sets opacity).
    if (shaftMatRef.current) shaftMatRef.current.color.set(color);
    if (headMatRef.current) headMatRef.current.color.set(color);
  }, [from, to, delayMs, color]);

  const reducedMotion = useRef(
    typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );

  useFrame(() => {
    const shaft = shaftRef.current;
    const head = headRef.current;
    if (!shaft || !head) return;
    if (reducedMotion.current) {
      shaft.scale.setScalar(1);
      head.scale.setScalar(1);
      if (shaftMatRef.current) shaftMatRef.current.opacity = FINAL_OPACITY;
      if (headMatRef.current) headMatRef.current.opacity = FINAL_OPACITY;
      return;
    }
    const elapsed = performance.now() - startedAtRef.current;
    // Before the delay elapses, render invisible (scale 0). After the
    // delay, ease-out cubic over ANIM_MS.
    if (elapsed < 0) {
      shaft.scale.setScalar(0);
      head.scale.setScalar(0);
      if (shaftMatRef.current) shaftMatRef.current.opacity = 0;
      if (headMatRef.current) headMatRef.current.opacity = 0;
      return;
    }
    const t = Math.min(elapsed / ANIM_MS, 1);
    // ease-out cubic
    const eased = 1 - (1 - t) ** 3;
    // Each mesh scales around its own center, so the shaft fades in around
    // the from-to midpoint and the head fades in around its own center.
    shaft.scale.setScalar(eased);
    head.scale.setScalar(eased);
    if (shaftMatRef.current) shaftMatRef.current.opacity = FINAL_OPACITY * eased;
    if (headMatRef.current) headMatRef.current.opacity = FINAL_OPACITY * eased;
  });

  const y = ARROW_Y + yOffset;
  return (
    <>
      {/* Shaft: cylinder height-aligned along the from→to bearing, centered
          on the midpoint of from and to. */}
      <mesh ref={shaftRef} position={[layout.midX, y, layout.midZ]} quaternion={layout.q}>
        <cylinderGeometry args={[SHAFT_RADIUS, SHAFT_RADIUS, layout.shaftLength, 8]} />
        <meshBasicMaterial
          ref={shaftMatRef}
          color={color}
          transparent
          opacity={0}
          depthWrite={false}
        />
      </mesh>
      {/* Head: cone apex pointing along the bearing, with its tip on `toPos`. */}
      <mesh ref={headRef} position={[layout.headX, y, layout.headZ]} quaternion={layout.q}>
        <coneGeometry args={[HEAD_RADIUS, HEAD_LENGTH, 12]} />
        <meshBasicMaterial
          ref={headMatRef}
          color={color}
          transparent
          opacity={0}
          depthWrite={false}
        />
      </mesh>
    </>
  );
}
